import { firstLoadPending } from '@/api/firstLoadPending';
import { retryQuery } from '@/api/restartQuery';
import { useCallback, useEffect, useMemo } from 'react';
import { useInfiniteQuery, useQuery, useQueryClient, type InfiniteData } from '@tanstack/react-query';
import { useMutation } from '@/api/mutation';
import {
  fetchStream,
  fetchUnreadCount,
  markRead,
  STREAM_MARK_READ_MUTATION_KEY,
  type StreamEntry,
  type StreamListResponse,
} from '@/api/stream';
import { useStreamUiStore } from '@/store';
import { useCurrentUser } from '@/hooks/useCurrentUser';

const POLL_INTERVAL = 30_000; // idle unread-count poll
const ACTIVE_POLL = 4_000; // fast poll while a job is in flight (pending entry)
const PANEL_POLL = 15_000; // keep the list fresh while the drawer is open
export const PAGE_SIZE = 50;

export const streamKeys = {
  all: ['stream'] as const,
  unreadCount: ['stream', 'unread-count'] as const,
  // Paged list (#1077). A NEW key: the old `['stream','list']` held a single
  // response; two data shapes on one key is the #479 class.
  listPages: ['stream', 'list-pages'] as const,
};

export interface UseStreamReturn {
  /** Number of unread stream entries (polled; speeds up while a job is live). */
  unreadCount: number;
  /** Stream entries — populated while the drawer is open or a job is in flight. */
  entries: StreamEntry[];
  /** Total count of entries (for pagination). */
  totalCount: number;
  /** Whether the entry list is currently loading. */
  isLoading: boolean;
  /** The first load of the entry list failed: no entries because the request failed, not because there are none. */
  listFailed: boolean;
  /** Try again for the failed list: re-sends the list request. */
  restartList: () => void;
  /** Force-refetch the full entry list (e.g. on drawer open). */
  refetchList: () => void;
  /** Mark specific entries as read by ID. */
  markEntriesRead: (entryIds: number[]) => void;
  /** Mark all entries as read. */
  markAllRead: () => void;
  /** The server still has unread entries beyond the pages loaded (#1077). */
  hasMoreUnread: boolean;
  /** Load the next page of entries. */
  loadMoreUnread: () => void;
  /** A further page is being fetched. */
  isLoadingMore: boolean;
  /** `unreadCount` from the latest list response (all unread, shown or not). */
  listUnreadCount: number;
}

type StreamPages = InfiniteData<StreamListResponse, number>;

/**
 * Next-page rule: relies on the server order (unread first, then newest first;
 * vingapi selectors.py), so the unread rows are always a prefix of the list.
 * More unread exist when the latest page's `unreadCount` exceeds the unread
 * rows loaded. The param is the number of entries loaded so far (the offset).
 */
export function nextStreamOffset(
  lastPage: StreamListResponse,
  allPages: StreamListResponse[],
): number | undefined {
  // #1076: a page without `entries` (an aborted response that slipped in) is skipped, never thrown on.
  const loaded = allPages.flatMap((p) => (Array.isArray(p?.entries) ? p.entries : []));
  const unreadLoaded = new Set(loaded.filter((e) => !e.viewed).map((e) => e.id)).size;
  return lastPage.unreadCount > unreadLoaded ? loaded.length : undefined;
}

export function useStream(): UseStreamReturn {
  const queryClient = useQueryClient();
  const panelOpen = useStreamUiStore((s) => s.panelOpen);
  const watching = useStreamUiStore((s) => s.watching);
  const stopWatching = useStreamUiStore((s) => s.stopWatching);
  // The stream is context-scoped: every /api/stream/* route resolves the
  // user's org/network context and answers 400 "No organization or network
  // context selected" without one. A user with no context (first SSO login,
  // before ForcedContextSelectionModal has been answered) must not be polled
  // for — the 400 is a client bug, not a transient, and it was being reported
  // as a client_crash on every first login (vingreact#64).
  const { currentContext } = useCurrentUser();
  const hasContext = currentContext !== null;

  // Unread count: idle poll every 30s, but speed up while a job we kicked off
  // is in flight so the badge reflects the pending → ready/failed flip promptly.
  const { data: unreadCount = 0 } = useQuery({
    queryKey: streamKeys.unreadCount,
    queryFn: fetchUnreadCount,
    enabled: hasContext,
    refetchInterval: watching ? ACTIVE_POLL : POLL_INTERVAL,
  });

  // The entry list is fetched when the drawer is open OR while we're watching an
  // in-flight job (so its pending entry can resolve in the background even with
  // the drawer closed). It fast-polls while anything is pending, drops to a
  // gentle keep-fresh cadence when the drawer is merely open, and idles
  // otherwise.
  const {
    data: listData,
    isPending: isLoadingPending, fetchStatus: isLoadingFetchStatus,
    isError: listIsError,
    refetch: refetchList,
    fetchNextPage,
    isFetchingNextPage,
    hasNextPage,
  } = useInfiniteQuery({
    queryKey: streamKeys.listPages,
    queryFn: ({ pageParam }) => fetchStream(PAGE_SIZE, pageParam),
    initialPageParam: 0,
    getNextPageParam: nextStreamOffset,
    enabled: hasContext && (panelOpen || watching),
    refetchInterval: (query) => {
      const hasPending = (query.state.data?.pages ?? []).some((p) =>
        Array.isArray(p?.entries) && p.entries.some((e) => e.status === 'pending'),
      );
      if (watching || hasPending) return ACTIVE_POLL;
      if (panelOpen) return PANEL_POLL;
      return false;
    },
  });
  // Not TanStack's `isLoading`: a first load paused offline is still waiting (#1569, #1561-g).
  const isLoading = firstLoadPending({ isPending: isLoadingPending, fetchStatus: isLoadingFetchStatus });
  const listFailed = listIsError && listData === undefined;

  // Flatten the pages; a shift between page requests (a new entry arriving)
  // can repeat a row, so de-duplicate by id, first occurrence wins.
  const entries = useMemo(() => {
    const seen = new Set<number>();
    const out: StreamEntry[] = [];
    for (const page of listData?.pages ?? []) {
      for (const e of Array.isArray(page?.entries) ? page.entries : []) {
        if (seen.has(e.id)) continue;
        seen.add(e.id);
        out.push(e);
      }
    }
    return out;
  }, [listData]);
  const pages = listData?.pages;
  const lastPage = pages?.[pages.length - 1];

  // Each open starts again from the first page: drop pages beyond the first
  // when the panel closes.
  useEffect(() => {
    if (panelOpen) return;
    queryClient.setQueryData<StreamPages>(streamKeys.listPages, (old) =>
      old && old.pages.length > 1
        ? { pages: old.pages.slice(0, 1), pageParams: old.pageParams.slice(0, 1) }
        : old,
    );
  }, [panelOpen, queryClient]);

  // Stop watching once the in-flight job has resolved — no `pending` entry
  // remains in the freshly-fetched list. Guarded so it only fires the
  // transition once.
  useEffect(() => {
    if (!watching || !listData) return;
    if (!entries.some((e) => e.status === 'pending')) {
      stopWatching();
    }
  }, [watching, listData, entries, stopWatching]);

  const markReadMutation = useMutation({
    mutationKey: STREAM_MARK_READ_MUTATION_KEY,
    // Mark-all and mark-some are two actions on one binding (#1628-d4).
    feedbackKeyOf: (entryIds) => (entryIds ? `some:${[...entryIds].sort((a, b) => a - b).join(',')}` : 'all'),
    mutationFn: (entryIds?: number[]) => markRead(entryIds),
    onSuccess: (_data, variables) => {
      void queryClient.invalidateQueries({ queryKey: streamKeys.unreadCount });
      queryClient.setQueryData<StreamPages>(streamKeys.listPages, (old) => {
        if (!old) return old;
        const ids = variables ? new Set(variables) : null;
        let newlyViewed = 0;
        const counted = new Set<number>();
        const mapped = old.pages.filter((page) => Array.isArray(page?.entries)).map((page) => ({
          ...page,
          entries: page.entries.map((e) => {
            if (e.viewed || (ids && !ids.has(e.id))) return e;
            if (!counted.has(e.id)) {
              counted.add(e.id);
              newlyViewed += 1;
            }
            return { ...e, viewed: true };
          }),
        }));
        // The server's unread total is not derivable from the loaded pages.
        return {
          ...old,
          pages: mapped.map((page) => ({
            ...page,
            unreadCount: ids ? Math.max(0, page.unreadCount - newlyViewed) : 0,
          })),
        };
      });
      void queryClient.refetchQueries({ queryKey: streamKeys.listPages });
    },
  });

  const markEntriesRead = useCallback(
    (entryIds: number[]) => markReadMutation.mutate(entryIds),
    [markReadMutation],
  );

  const markAllRead = useCallback(
    () => markReadMutation.mutate(undefined),
    [markReadMutation],
  );

  return {
    unreadCount,
    entries,
    totalCount: lastPage?.totalCount ?? 0,
    isLoading,
    listFailed,
    restartList: () => { void retryQuery(queryClient, streamKeys.listPages); },
    refetchList: () => { void refetchList(); },
    markEntriesRead,
    markAllRead,
    hasMoreUnread: hasNextPage,
    loadMoreUnread: () => {
      if (!isFetchingNextPage) void fetchNextPage();
    },
    isLoadingMore: isFetchingNextPage,
    listUnreadCount: lastPage?.unreadCount ?? 0,
  };
}
